import type {DelimiterTokenType, List, Nodes} from 'mdast'
import type {Extension} from 'mdast-util-from-markdown'
import {visit} from 'unist-util-visit'
import {visitParents} from 'unist-util-visit-parents'

import {
  insertChild,
  isAdjacent,
  popChild,
  pushChild,
  removeChild,
  reverseChildrenEntries,
  unshiftChild,
} from '../../syntax-tree/tree-manipulation-utils'
import type {ParentsOf} from '../../syntax-tree/type-utils'
import {phrasingContentNodeTypes} from '../node-types'

/**
 * Types of delimiter tokens that should be treated as prefixes. These are the delimiter tokens for multiline Markdown
 * nodes that have prefixes that repeat on every line, like blockquotes.
 */
export const prefixTokenTypes: ReadonlySet<DelimiterTokenType> = new Set([
  'listItemIndent',
  'listItemPrefix',
  'blockQuotePrefix',
  'linePrefix',
  'taskListCheck',
])

/**
 * Moves delimiters into the blocks they correspond to, to make them easier to control and style. For example,
 * blockquote prefixes get moved into the blockquote nodes. This is done recursively, so the blockquote prefixes
 * actually get moved into the corresponding _paragraph_ node inside the blockquote node.
 */
export function mdastContainDelimiters(): Extension {
  return {
    transforms: [containDelimiters],
  }
}

/** Check if the node is a 'container' that we can put prefixes into. */
function isContainer(node: Nodes) {
  // We exclude phrasing content so we don't contain delimiters into nodes where it stops making sense. Ie, for this
  // markdown `> **strong**` we want a `blockquote > paragraph > delimiter, strong` structure, not
  // `blockquote > paragraph > strong > delimiter` - the blockquote delimiter doesn't belong in the strong node.
  return !(phrasingContentNodeTypes as Set<Nodes['type']>).has(node.type)
}

/**
 * There's a weird edge case (bug?) in the parser where blockquote delimiters will end up trailing inside a list.
 * Sometimes we want these to end up in the last list item (ie `> - item\n>   ` where we have a trailing list item
 * indent), which we'll handle later, but other times they should be in the parent blockquote
 * (ie `> - item\n> > nested quote`). So we first walk the tree and move any trailing prefixes out of lists and
 * into the parent node, but only if they don't have listItemIndent tokens. :sob:
 */
function liftTrailingQuotePrefixesFromLists(tree: Nodes): void {
  visitParents(tree, 'list', (list, parents) => {
    const parent = parents.at(-1) as ParentsOf<List> | undefined
    const targetIndex = (parent?.children?.indexOf(list) ?? -1) + 1
    if (!parent || targetIndex === 0) return

    let lastChild = list.children.at(-1)
    while (lastChild?.type === 'delimiter' && lastChild.tokenType === 'blockQuotePrefix') {
      popChild(list, true)
      insertChild(parent, targetIndex, lastChild)
      lastChild = list.children.at(-1)
    }
  })
}

function containDelimiters(tree: Nodes): void {
  liftTrailingQuotePrefixesFromLists(tree)

  visit(tree, item => {
    let nextChild: Nodes | null = null
    for (const [i, child] of reverseChildrenEntries(item)) {
      // Move all prefix nodes into the beginning of the node that immediately follows them
      if (
        child.type === 'delimiter' &&
        prefixTokenTypes.has(child.tokenType) &&
        nextChild &&
        isAdjacent(child, nextChild) &&
        isContainer(nextChild)
      ) {
        removeChild(item, i)
        unshiftChild(nextChild, child)
        continue // keep nextChild the same since the current child got removed
      }

      // Specific logic for normalizing list items so that lists only contain items as direct children
      if (child.type === 'listItem') {
        // In `liftTrailingQuotePrefixesFromLists` we lifted trailing blockquote prefixes, but only when not followed by
        // list item indent prefixes. Now we handle the other case, moving any remaining trailing prefixes into the last item
        while (nextChild?.type === 'delimiter' && prefixTokenTypes.has(nextChild.tokenType)) {
          removeChild(item, i + 1)
          pushChild(child, nextChild)
          nextChild = item.children?.[i + 1] ?? null
        }
      }

      nextChild = child
    }
  })
}
